import {
  type ContentNode,
  ContentNodeUtils,
  type DataTypeConfig,
  DataTypeConfigUtils,
  type DataTypeLocator,
  type DataTypeState,
  type FormattingState,
} from '@helo/cms'
import { Assert, If, Value, flow, pipe } from '@helo/utils'

import { Format } from '../../Format'
import { Options } from '../../Options'
import { RenderPipeline } from '../../RenderPipeline'
import type { ChartDataPoint, ChartDataSlice } from './ChartData'
import {
  type ChartDataFilter,
  type ChartDataSort,
  type ChartNullValueBehavior,
  chartDataOptions,
} from './ChartData.options'

const isMissingMeasure = (raw: unknown): boolean =>
  raw == null || raw === '' || Number.isNaN(Number(raw))

/** List rows live on LIST nodes; dashboard queries arrive as FIELD of LIST. */
const getListContentNode = (contentNode: ContentNode) =>
  pipe(
    contentNode,
    If.is(ContentNodeUtils.isKind('FIELD'), ContentNodeUtils.getOfType()),
    Assert.orThrow(
      ContentNodeUtils.isKind('LIST'),
      () => new Error('Chart content node must be a LIST or FIELD of LIST'),
    ),
  )

const valueAtLocator =
  (locator: DataTypeLocator) =>
  (row: ContentNode): unknown =>
    pipe(
      row,
      ContentNodeUtils.findContentNode(locator),
      ContentNodeUtils.getData(),
    )

const projectRetrievedRows = ({
  rows,
  dimensionLocator,
  dimensionKey,
  measures,
}: {
  rows: ContentNode[]
  dimensionLocator: DataTypeLocator
  dimensionKey: string
  measures: Array<{ locator: DataTypeLocator; key: string }>
}): Record<string, unknown>[] =>
  rows.map(row => {
    const projected: Record<string, unknown> = {
      [dimensionKey]: valueAtLocator(dimensionLocator)(row),
    }
    for (const measure of measures) {
      projected[measure.key] = valueAtLocator(measure.locator)(row)
    }
    return projected
  })

const normalizePoints = (
  rows: Record<string, unknown>[],
  dimensionKey: string,
  dataKey: string,
  behavior: ChartNullValueBehavior,
): ChartDataPoint[] => {
  const points: ChartDataPoint[] = []

  for (const row of rows) {
    const x = String(row[dimensionKey] ?? '')
    const raw = row[dataKey]

    if (isMissingMeasure(raw)) {
      if (behavior === 'gap') points.push({ x, y: null })
      continue
    }

    points.push({ x, y: Number(raw) })
  }

  return points
}

type ProcessChartDataOptions = {
  rows: Record<string, unknown>[]
  dimensionKey: string
  measureKey: string
  dataFilter?: ChartDataFilter
  dataSort: ChartDataSort
  dataTopN?: number
}

const numericMeasure = (
  row: Record<string, unknown>,
  measureKey: string,
): number => {
  const raw = row[measureKey]
  if (isMissingMeasure(raw)) return Number.NEGATIVE_INFINITY
  const value = Number(raw)
  return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY
}

const processChartData = ({
  rows,
  dimensionKey,
  measureKey,
  dataFilter,
  dataSort,
  dataTopN,
}: ProcessChartDataOptions): Record<string, unknown>[] => {
  const allowedCategories =
    dataFilter == null ? undefined : new Set(dataFilter.map(String))
  let result =
    allowedCategories == null
      ? [...rows]
      : rows.filter(row =>
          allowedCategories.has(String(row[dimensionKey] ?? '')),
        )

  if (dataTopN != null) {
    const topRows = new Set(
      [...result]
        .sort(
          (left, right) =>
            numericMeasure(right, measureKey) -
            numericMeasure(left, measureKey),
        )
        .slice(0, Math.max(0, Math.floor(dataTopN))),
    )
    result = result.filter(row => topRows.has(row))
  }

  if (dataSort === 'natural') return result

  return result.sort((left, right) => {
    if (dataSort === 'dimension_asc' || dataSort === 'dimension_desc') {
      const comparison = String(left[dimensionKey] ?? '').localeCompare(
        String(right[dimensionKey] ?? ''),
      )
      return dataSort === 'dimension_asc' ? comparison : -comparison
    }

    const comparison =
      numericMeasure(left, measureKey) - numericMeasure(right, measureKey)
    return dataSort === 'measure_asc' ? comparison : -comparison
  })
}

const getFieldLabel = (state: DataTypeState) => {
  if (typeof state.label === 'string') return state.label
  return undefined
}

const formatMeasureDisplayValue =
  (formattingState: FormattingState) =>
  (value: number): string => {
    try {
      const parts = Format.numberParts(formattingState)(value)
      return `${parts.prefix}${parts.value}${parts.suffix}`
    } catch {
      return String(value)
    }
  }

const createMeasureDisplayFormatter = ({
  fieldState,
  seriesOverrides = {},
  rootFormat = {},
}: {
  fieldState: DataTypeState
  seriesOverrides?: object
  rootFormat?: object
}) => {
  const formattingState = {
    ...fieldState,
    ...rootFormat,
    ...seriesOverrides,
  }
  return formatMeasureDisplayValue(formattingState)
}

const getChartState =
  () =>
  (ctx: { output: { contentNode?: ContentNode | null } }): object =>
    pipe(
      ctx.output.contentNode,
      Assert.orThrow(Value.isNotNullable),
      getListContentNode,
      ContentNodeUtils.getState(),
    )

const getNestedState =
  (key: string) =>
  (ctx: { output: { contentNode?: ContentNode | null } }): object => {
    const state = getChartState()(ctx) as Record<string, unknown>
    const nested = state[key]
    return nested != null &&
      typeof nested === 'object' &&
      !Array.isArray(nested)
      ? (nested as object)
      : {}
  }

const findField = (locator: DataTypeLocator) =>
  flow(
    DataTypeConfigUtils.find(locator),
    Assert.orThrow(DataTypeConfigUtils.isKind('FIELD')),
  )

const findFieldKey = (locator: DataTypeLocator) =>
  flow(findField(locator), DataTypeConfigUtils.getKey())

type SeriesInputItem = {
  measureLocator?: DataTypeLocator
}

type ChartDataSetupCtx = {
  input: {
    dimensionLocator: DataTypeLocator
    dataFilter?: ChartDataFilter
    dataSort: ChartDataSort
    nullValueBehavior: ChartNullValueBehavior
    _series?: SeriesInputItem[]
  }
  output: {
    contentNode?: ContentNode | null
    data?: ChartDataSlice
  }
}

/** Resolve root input: override, then content-node state, then defaults. */
const setupInput = () =>
  RenderPipeline.updateInput((input, ctx) =>
    pipe(
      input,
      Options.fallback(chartDataOptions, getChartState()(ctx)),
      Options.applyDefaults(chartDataOptions),
      Options.validate(chartDataOptions),
    ),
  )

const setData = () =>
  RenderPipeline.updateOutput((output: ChartDataSetupCtx['output'], ctx) => {
    const contentNode = output.contentNode
    if (contentNode == null) {
      throw new Error('Chart requires a content node from Query.setup')
    }

    const listContentNode = getListContentNode(contentNode)
    const rootDataTypeConfig = pipe(
      listContentNode,
      ContentNodeUtils.getDataTypeConfig(),
    )
    const dimensionDataTypeConfig = findField(ctx.input.dimensionLocator)(
      rootDataTypeConfig,
    )
    const dimensionKey = findFieldKey(ctx.input.dimensionLocator)(
      rootDataTypeConfig,
    )
    const dimensionLabel = pipe(
      dimensionDataTypeConfig,
      DataTypeConfigUtils.getState(),
      getFieldLabel,
    )
    const retrievedRows = pipe(
      listContentNode,
      ContentNodeUtils.getItems(),
      If.isElse(
        Value.isArray,
        items => items as ContentNode[],
        () => [] as ContentNode[],
      ),
    )
    const measures = (ctx.input._series ?? []).flatMap(seriesItem => {
      if (seriesItem.measureLocator == null) return []
      const key = findFieldKey(seriesItem.measureLocator)(rootDataTypeConfig)
      return [{ locator: seriesItem.measureLocator, key }]
    })
    const projectedRows = projectRetrievedRows({
      rows: retrievedRows,
      dimensionLocator: ctx.input.dimensionLocator,
      dimensionKey,
      measures,
    })
    const chartData = processChartData({
      rows: projectedRows,
      dimensionKey,
      measureKey: measures[0]?.key ?? dimensionKey,
      dataFilter: ctx.input.dataFilter,
      dataSort: ctx.input.dataSort,
    })

    return {
      data: {
        chartData,
        dimensionLocator: ctx.input.dimensionLocator,
        dimensionKey,
        dimensionLabel,
        nullValueBehavior: ctx.input.nullValueBehavior,
      },
    }
  })

export const ChartDataParts = {
  isMissingMeasure,
  getListContentNode,
  valueAtLocator,
  projectRetrievedRows,
  normalizePoints,
  processChartData,
  getFieldLabel,
  createMeasureDisplayFormatter,
  formatMeasureDisplayValue,
  getChartState,
  getNestedState,
  findField,
  findFieldKey,
}

export const ChartDataInputParts = {
  setupInput,
}

export const ChartDataOutputParts = {
  setData,
}

export const ChartDataUtils = ChartDataParts
